// @file Manages state and logic for the whiteboard element modifier UI, which allows modifying properties of selected shapes
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getPollFromPost, isPollClosed } from '@@/bits/surface_polls'
import {
  ActionType,
  getAllShapesInGroup,
  getCommonShapeProperty,
  getLongPressedShape,
  isGroupShape,
  isShapeChildOfGroup,
  shouldEnableGroupingForShapes,
} from '@@/bits/whiteboard'
import type { ConnectorStyle } from '@@/bits/whiteboard_element_modifier'
import {
  closePollRow,
  copyRow,
  cutRow,
  deleteRow,
  duplicateRow,
  editPollRow,
  exportPollRow,
  freezeRow,
  groupRow,
  isShapeWithRichText,
  mapTldrawColorToWhiteboardColor,
  openPollRow,
  pasteRow,
  selectAllRow,
  sendToBackRow,
  sendToFrontRow,
  TextFormattingOption,
  textFormattingOptions,
  THEME_FONT_MENU_ITEM_ID,
  unfreezeRow,
  ungroupRow,
} from '@@/bits/whiteboard_element_modifier'
import type {
  TldrawDefaultColorStyleColor,
  TldrawGeoShapeGeoStyle,
  ToolTailwindColorType,
} from '@@/bits/whiteboard_tools'
import { arrowToolColors, geoToolColors, getAllTools } from '@@/bits/whiteboard_tools'
import type { Anchor } from '@@/library/v4/components/OzBasePopover.vue'
import type { OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardLinkingStore } from '@@/pinia/whiteboard_linking'
import { useWhiteboardPanelsStore } from '@@/pinia/whiteboard_panels'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWhiteboardSpecialEffectsStore } from '@@/pinia/whiteboard_special_effects'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import type { WhiteboardTextSize, WhiteboardTool } from '@@/types/whiteboard'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { useWhiteboardHeadlessTiptapEditor } from '@@/vuecomposables/use_whiteboard_headless_tiptap_editor'
import { useWhiteboardShapeHandlers } from '@@/vuecomposables/whiteboard_shape_handlers'
import { LongPressEvent } from '@@/vuedirectives/emit_long_press'
import { device } from '@padlet/universal-post-editor/src/bits/device'
import type { PadletAttachmentShape } from '@padlet/whiteboard-schema'
import type { Content } from '@padlet/whiteboard-tiptap'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import type { TLArrowShapeArrowheadStyle, TLDefaultDashStyle, TLDefaultFontStyle, TLShape } from 'tldraw'
import { computed, ref, shallowRef } from 'vue'

export const useWhiteboardElementModifierStore = defineStore('whiteboardElementModifier', () => {
  const whiteboardStore = useWhiteboardStore()
  const whiteboardPanelsStore = useWhiteboardPanelsStore()
  const whiteboardToolsStore = useWhiteboardToolsStore()
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const whiteboardLinkingStore = useWhiteboardLinkingStore()
  const whiteboardSpecialEffectsStore = useWhiteboardSpecialEffectsStore()
  const surfacePostsStore = useSurfacePostsStore()

  const selectedShapes = shallowRef<TLShape[]>([])

  const onlySelectedShape = computed<TLShape | null>(() => {
    return selectedShapes.value.length === 1 ? selectedShapes.value[0] : null
  })

  useTldrawEditor().react((editor) => {
    selectedShapes.value = editor.getSelectedShapes()
  })

  // #region Context menu

  const contextMenuGroups = ref<OzMenuRowsRow[][]>([])
  const contextMenuAnchor = ref<Anchor>()
  const xContextMenu = ref(false)

  const areSelectedShapesTypesAllowedToBeCreated = computed(() => {
    return (
      whiteboardPermissionsStore.canIAdminister ||
      selectedShapes.value.every((shape) => whiteboardToolsStore.wasShapeCreatedFromAllowedTool(shape))
    )
  })

  const areAllSelectedShapesEditable = computed(() => {
    const editor = useTldrawEditor().editor
    if (editor == null) return false
    const whiteboardShapeHandlers = useWhiteboardShapeHandlers(editor)
    return selectedShapes.value.every((shape) => whiteboardShapeHandlers.checkCanEdit(shape))
  })

  const linkRow = computed(() => {
    const hasLinking = whiteboardLinkingStore.checkIfLinkingExists(onlySelectedShape.value?.id ?? '')
    const linkingContextMenuText = hasLinking ? __('Edit link') : __('Add link')
    return {
      icon: 'link',
      text: linkingContextMenuText,
      eventPayload: ActionType.Link,
      rightLabel: '⇧L',
      shouldShow:
        onlySelectedShape.value != null &&
        onlySelectedShape.value?.type !== 'arrow' &&
        !whiteboardStore.isFrameBreakout &&
        !isShapeChildOfGroup(onlySelectedShape.value) &&
        // Don't show linking option for polls unless they already have a link attached
        (!isOnlySelectedShapePoll.value || hasLinking),
    }
  })

  const specialEffectsRow = computed(() => {
    const specialEffectsMenuText =
      onlySelectedShape.value == null
        ? ''
        : whiteboardSpecialEffectsStore.doesShapeHaveSpecialEffects(onlySelectedShape.value.id)
        ? __('Edit special effects')
        : __('Add special effects')
    return {
      icon: 'fx_squircle',
      text: specialEffectsMenuText,
      eventPayload: ActionType.SpecialEffect,
      shouldShow:
        selectedShapes.value.length === 1 &&
        onlySelectedShape.value != null &&
        whiteboardSpecialEffectsStore.canAddSpecialEffectForShape(onlySelectedShape.value),
    }
  })

  const isOnlySelectedShapePoll = computed(() => {
    return (
      onlySelectedShape.value != null &&
      onlySelectedShape.value.type === 'padlet-attachment-shape' &&
      (onlySelectedShape.value as PadletAttachmentShape).props.isPoll
    )
  })

  const isOnlySelectedShapePollClosed = computed(() => {
    if (!isOnlySelectedShapePoll.value) return false
    const postId = (onlySelectedShape.value as PadletAttachmentShape).props.postId
    const poll = getPollFromPost(surfacePostsStore.postEntitiesById[postId])
    return poll != null && isPollClosed(poll)
  })

  function showContextMenu(): void {
    xContextMenu.value = true
  }

  function hideContextMenu(): void {
    xContextMenu.value = false
  }

  function buildContextMenu(e: PointerEvent | LongPressEvent): void {
    if (e instanceof LongPressEvent && !device.touchable) {
      // Only open context menu on long press for touch devices
      return
    }

    if (
      whiteboardStore.currentFrameId == null ||
      !whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId)
    ) {
      return
    }
    if (!isAppUsing('sandboxRetainActiveTool') && whiteboardStore.selectedToolId !== 'select') {
      return
    }
    if (whiteboardPanelsStore.xCardTemplatesPanel) {
      return
    }

    const pointerEventCoords = { x: e.x, y: e.y }

    // Only look for long pressed shape if tldraw detects no selected shapes.
    // This can be when the shape is locked (frozen) -- tldraw doesn't set selected state for locked shapes.
    if (selectedShapes.value.length === 0) {
      const longPressedShape = getLongPressedShape(e)
      selectedShapes.value = longPressedShape != null ? [longPressedShape] : []
    }

    contextMenuAnchor.value = pointerEventCoords
    showContextMenu()

    const hasSelectedShapes = selectedShapes.value.length > 0
    if (!hasSelectedShapes) {
      contextMenuGroups.value = [[pasteRow], [selectAllRow]]
      return
    }

    const areAllSelectedShapesFrozen = selectedShapes.value.every((shape) => shape.isLocked)
    if (areAllSelectedShapesFrozen) {
      contextMenuGroups.value = [[unfreezeRow]]
      return
    }

    if (!areAllSelectedShapesEditable.value) {
      // If the selected shapes are not allowed to be created anymore (Allowed tools settings),
      // we hide "Copy" and "Duplicate" options.
      if (!areSelectedShapesTypesAllowedToBeCreated.value) {
        contextMenuGroups.value = [[pasteRow, selectAllRow]]
        return
      }
      contextMenuGroups.value = [[copyRow, pasteRow, duplicateRow, selectAllRow]]
      return
    }

    contextMenuGroups.value = [
      [
        { ...editPollRow, shouldShow: isOnlySelectedShapePoll.value },
        { ...openPollRow, shouldShow: isOnlySelectedShapePoll.value && isOnlySelectedShapePollClosed.value },
        { ...closePollRow, shouldShow: isOnlySelectedShapePoll.value && !isOnlySelectedShapePollClosed.value },
        { ...exportPollRow, shouldShow: isOnlySelectedShapePoll.value },
      ].filter((item) => item.shouldShow),
      [
        { ...sendToFrontRow, shouldShow: true },
        { ...sendToBackRow, shouldShow: true },
        {
          ...groupRow,
          shouldShow: shouldEnableGroupingForShapes(selectedShapes.value) && selectedShapes.value.length > 1,
        },
        {
          ...ungroupRow,
          shouldShow:
            shouldEnableGroupingForShapes(selectedShapes.value) &&
            selectedShapes.value.length === 1 &&
            isGroupShape(selectedShapes.value[0]),
        },
      ].filter((item) => item.shouldShow),
      [linkRow.value, specialEffectsRow.value].filter((item) => item.shouldShow),
      [
        // If the user can edit shapes but they can't add new shapes of this type,
        // we hide "Cut", "Copy" and "Duplicate".
        { ...cutRow, shouldShow: areSelectedShapesTypesAllowedToBeCreated.value },
        { ...copyRow, shouldShow: areSelectedShapesTypesAllowedToBeCreated.value },
        { ...pasteRow, shouldShow: true },
        { ...duplicateRow, shouldShow: areSelectedShapesTypesAllowedToBeCreated.value },
      ].filter((row) => row.shouldShow),
      [
        {
          ...freezeRow,
          shouldShow: !isShapeChildOfGroup(onlySelectedShape.value as TLShape),
        },
        { ...deleteRow, shouldShow: true },
      ].filter((item) => item.shouldShow),
      [selectAllRow],
    ].filter((group) => !isEmpty(group))
  }

  // #endregion

  // #region Element modifier menu

  const elementModifierShapeTool = computed<WhiteboardTool | null>(() => {
    if (onlySelectedShape.value?.typeName !== 'shape') {
      return null
    }
    // Find the tool that matches the selected shape
    const tool = getAllTools().find((tool) => tool.tldrawToolId === onlySelectedShape.value?.type)
    if (tool == null) {
      return null
    }
    return {
      ...tool,
    }
  })

  const elementModifierShapeToolState = computed(() => {
    const tool = elementModifierShapeTool.value
    if (tool == null) {
      return null
    }
    const secondaryMenuItems = tool.secondaryMenu?.items ?? []
    let currentSecondarySelectionId: string | undefined
    // Set the current selected secondary tool for the element modifier
    if (onlySelectedShape.value?.type !== 'geo') {
      // Non-geo
      const currentShapeColor: TldrawDefaultColorStyleColor | undefined = (onlySelectedShape.value?.props as any)?.color
      currentSecondarySelectionId =
        currentShapeColor != null ? mapTldrawColorToWhiteboardColor(secondaryMenuItems, currentShapeColor) : undefined
    } else {
      // Geo
      const currentGeoStyle: TldrawGeoShapeGeoStyle | undefined = (onlySelectedShape.value?.props as any)?.geo
      currentSecondarySelectionId = currentGeoStyle
    }
    return { currentSecondarySelectionId }
  })

  const selectedShapeColor = computed(() => {
    if (
      onlySelectedShape.value?.type === 'geo' ||
      onlySelectedShape.value?.type === 'arrow' ||
      onlySelectedShape.value?.type === 'padlet-speech-bubble'
    ) {
      const currentShapeColor: TldrawDefaultColorStyleColor | undefined = (onlySelectedShape.value?.props as any)?.color
      if (currentShapeColor == null) {
        return undefined
      }
      return onlySelectedShape.value?.type === 'geo' || onlySelectedShape.value?.type === 'padlet-speech-bubble'
        ? mapTldrawColorToWhiteboardColor(geoToolColors, currentShapeColor)
        : mapTldrawColorToWhiteboardColor(arrowToolColors, currentShapeColor)
    } else if (onlySelectedShape.value?.type !== 'geo') {
      return elementModifierShapeToolState.value?.currentSecondarySelectionId as ToolTailwindColorType | undefined
    }
    return undefined
  })

  const selectedShapeFillStyle = computed(() => {
    const isGeoShape = onlySelectedShape.value?.type === 'geo'
    const isPadletSpeechBubble = onlySelectedShape.value?.type === 'padlet-speech-bubble'
    if (!isGeoShape && !isPadletSpeechBubble) {
      return undefined
    }
    const currentShapeFillStyle: 'none' | 'semi' | 'solid' | undefined = (onlySelectedShape.value?.props as any)?.fill
    return currentShapeFillStyle
  })

  const selectedShapeGeoStyle = computed(() => {
    const isGeoShape = onlySelectedShape.value?.type === 'geo'
    if (!isGeoShape) {
      return undefined
    }
    const currentShapeGeoStyle: TldrawGeoShapeGeoStyle | undefined = (onlySelectedShape.value?.props as any)?.geo
    return currentShapeGeoStyle
  })

  const allSelectedShapes = computed(() => {
    return selectedShapes.value.flatMap((shape) => (isGroupShape(shape) ? getAllShapesInGroup(shape) : [shape]))
  })

  const isMultipleSelectedShapes = computed<boolean>(() => {
    return isAppUsing('sandboxTextFonts') && allSelectedShapes.value.length > 1
  })

  const selectedMultipleShapesSize = computed<WhiteboardTextSize | null>(() => {
    if (allSelectedShapes.value.every((shape) => shape.meta.autoSize === true)) return 'auto'
    if (allSelectedShapes.value.some((shape) => shape.meta.autoSize === true)) return null
    return getCommonShapeProperty({
      shapes: allSelectedShapes.value,
      property: 'size',
    })
  })

  const selectedShapeSize = computed<WhiteboardTextSize | null>(() => {
    if (onlySelectedShape.value == null) return null
    if ('size' in onlySelectedShape.value.props) {
      return onlySelectedShape.value.props.size
    }

    if (isGroupShape(onlySelectedShape.value)) {
      const allShapesInGroup = getAllShapesInGroup(onlySelectedShape.value)
      return getCommonShapeProperty({
        shapes: allShapesInGroup,
        property: 'size',
      })
    }

    return null
  })

  const selectedShapeScale = computed(() => {
    if (onlySelectedShape.value != null && 'scale' in onlySelectedShape.value.props) {
      return onlySelectedShape.value.props.scale
    }
    return null
  })

  const isSelectedShapeAutoSize = computed(() => {
    return (
      (onlySelectedShape.value?.type !== 'note' &&
        selectedShapeScale.value != null &&
        selectedShapeScale.value !== 1) ||
      (onlySelectedShape.value?.meta?.autoSize as boolean)
    )
  })

  const selectedSize = computed<WhiteboardTextSize | null>(() => {
    if (isMultipleSelectedShapes.value) return selectedMultipleShapesSize.value
    return isSelectedShapeAutoSize.value ? 'auto' : selectedShapeSize.value
  })

  const selectedShapeFontName = computed(() => {
    if (onlySelectedShape.value == null) return null
    if (onlySelectedShape.value.meta.useThemeFont === true) {
      return THEME_FONT_MENU_ITEM_ID
    }
    if ('font' in onlySelectedShape.value.props) {
      return onlySelectedShape.value.props.font
    }

    if (isGroupShape(onlySelectedShape.value)) {
      return getCommonShapeProperty<TLDefaultFontStyle>({
        shapes: getAllShapesInGroup(onlySelectedShape.value),
        property: 'font',
      })
    }
    return null
  })

  const selectedMultipleShapesFontName = computed(() => {
    const allShapesUsingThemeFont = allSelectedShapes.value.every((shape) => shape.meta.useThemeFont)
    if (allShapesUsingThemeFont) {
      return THEME_FONT_MENU_ITEM_ID
    }
    return getCommonShapeProperty<TLDefaultFontStyle>({
      shapes: allSelectedShapes.value,
      property: 'font',
    })
  })

  const selectedFontName = computed(() => {
    return isMultipleSelectedShapes.value ? selectedMultipleShapesFontName.value : selectedShapeFontName.value
  })

  const selectedShapeConnectorStyle = computed<ConnectorStyle | undefined>(() => {
    const isArrowShape = onlySelectedShape.value?.type === 'arrow'
    if (!isArrowShape) {
      return undefined
    }
    const currentShapeArrowheadStartStyle: TLArrowShapeArrowheadStyle = (onlySelectedShape.value?.props as any)
      ?.arrowheadStart
    const currentShapeArrowheadEndStyle: TLArrowShapeArrowheadStyle = (onlySelectedShape.value?.props as any)
      ?.arrowheadEnd
    const currentShapeDashStyle: TLDefaultDashStyle = (onlySelectedShape.value?.props as any)?.dash
    return [currentShapeArrowheadStartStyle, currentShapeDashStyle, currentShapeArrowheadEndStyle]
  })

  const nonEmptyTextShapes = computed(() => {
    // Filter out empty text shapes to allow formatting operations (bold, italic, strikethrough) on non-empty text shapes within a group selection
    return allSelectedShapes.value?.filter((shape) => {
      if (!isShapeWithRichText(shape) || !('richText' in shape.props)) return false
      return !useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isEmpty
    })
  })

  const isAllSelectedShapesTextBold = computed(() => {
    return nonEmptyTextShapes.value.every((shape) => {
      if (!('richText' in shape.props)) return false
      return useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isActive('bold')
    })
  })

  const isAllSelectedShapesTextItalic = computed(() => {
    return nonEmptyTextShapes.value.every((shape) => {
      if (!isShapeWithRichText(shape) || !('richText' in shape.props)) return false
      return useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isActive(
        'italic',
      )
    })
  })

  const isAllSelectedShapesTextStrikethrough = computed(() => {
    return nonEmptyTextShapes.value.every((shape) => {
      if (!isShapeWithRichText(shape) || !('richText' in shape.props)) return false
      return useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isActive(
        'strike',
      )
    })
  })

  const isAllSelectedShapesBulletedList = computed(() => {
    return nonEmptyTextShapes.value.every((shape) => {
      if (!isShapeWithRichText(shape) || !('richText' in shape.props)) return false
      return useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isActive(
        'bulletList',
      )
    })
  })

  const isAllSelectedShapesNumberedList = computed(() => {
    return nonEmptyTextShapes.value.every((shape) => {
      if (!isShapeWithRichText(shape) || !('richText' in shape.props)) return false
      return useWhiteboardHeadlessTiptapEditor(shape.props.richText as Content).whiteboardTiptapEditor.isActive(
        'orderedList',
      )
    })
  })

  const textFormattingItems = computed<WhiteboardTool[]>(() => {
    return [
      {
        ...textFormattingOptions[TextFormattingOption.Bold],
        isSelected: isAllSelectedShapesTextBold.value,
      },
      {
        ...textFormattingOptions[TextFormattingOption.Italic],
        isSelected: isAllSelectedShapesTextItalic.value,
      },
      {
        ...textFormattingOptions[TextFormattingOption.Strikethrough],
        isSelected: isAllSelectedShapesTextStrikethrough.value,
        xDivider: true,
      },
      {
        ...textFormattingOptions[TextFormattingOption.BulletedList],
        isSelected: isAllSelectedShapesBulletedList.value,
      },
      {
        ...textFormattingOptions[TextFormattingOption.NumberedList],
        isSelected: isAllSelectedShapesNumberedList.value,
        xDivider: true,
      },
    ]
  })

  // #endregion

  return {
    onlySelectedShape,
    selectedShapes,

    // Context menu
    xContextMenu,
    contextMenuGroups,
    contextMenuAnchor,
    isOnlySelectedShapePollClosed,
    isOnlySelectedShapePoll,
    showContextMenu,
    hideContextMenu,
    buildContextMenu,

    // Element modifier menu
    elementModifierShapeTool,
    elementModifierShapeToolState,
    selectedShapeColor,
    selectedShapeFillStyle,
    selectedShapeGeoStyle,
    allSelectedShapes,
    isMultipleSelectedShapes,
    selectedMultipleShapesSize,
    selectedSize,
    selectedFontName,
    selectedShapeConnectorStyle,
    isAllSelectedShapesTextBold,
    isAllSelectedShapesTextItalic,
    isAllSelectedShapesTextStrikethrough,
    textFormattingItems,
  }
})
